import { CSSProperties, useCallback, useEffect, useRef, useState } from "react";

export default function useTooltip() {
  const [isHovered, setIsHovered] = useState(false);
  const itemRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltipPosition, setTooltipPosition] = useState<CSSProperties>({});

  useEffect(() => {
    if (!isHovered || !itemRef.current) return;

    const calculatePosition = () => {
      if (!itemRef.current || !tooltipRef.current) return;

      const rect = itemRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Отступ от элемента
      const offset = 8;

      // Доступное пространство с каждой стороны
      const space = {
        right: viewportWidth - rect.right,
        left: rect.left,
        bottom: viewportHeight - rect.bottom,
        top: rect.top,
      };

      // Рассчитываем позицию по умолчанию (справа снизу)
      let left = rect.right + offset;
      let top = rect.top;
      let transformOrigin = "left top";

      // Проверяем, помещается ли тултип справа
      if (space.right < tooltipRect.width) {
        // Если не помещается справа, показываем слева
        left = rect.left - tooltipRect.width - offset;
        transformOrigin = "right top";
      }

      // Проверяем, помещается ли тултип снизу
      if (space.bottom < tooltipRect.height) {
        // Если не помещается снизу, показываем сверху
        top = rect.bottom - tooltipRect.height;
        transformOrigin = transformOrigin.replace("top", "bottom");
      }

      // Гарантируем, что тултип не выйдет за границы экрана
      left = Math.max(offset, Math.min(left, viewportWidth - tooltipRect.width - offset));
      top = Math.max(offset, Math.min(top, viewportHeight - tooltipRect.height - offset));

      setTooltipPosition({
        left,
        top,
        transformOrigin,
      });
    };

    calculatePosition();
    window.addEventListener("resize", calculatePosition);
    window.addEventListener("scroll", calculatePosition, true);

    // eslint-disable-next-line consistent-return
    return () => {
      window.removeEventListener("resize", calculatePosition);
      window.removeEventListener("scroll", calculatePosition, true);
    };
  }, [isHovered]);

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  return { itemRef, tooltipRef, tooltipPosition, handleMouseEnter, handleMouseLeave, isHovered };
}
